import { Link } from "react-router-dom";
import { Field, Input, Select, Textarea } from "./ui";
import { InstallmentPlanEditor } from "./InstallmentPlan";
import { KhataSummary } from "./KhataSummary";
import { calcSalePreview, formatMoney, roundMoney } from "../utils/format";
import { PaymentFields } from "./PaymentFields";
import { COPY } from "../utils/copy";
import { emptyProof, isoDate, proofFromRecord, withProof } from "../utils/finance";
import { customPlanPayload, seedEqualRows } from "../utils/plan";

export const emptySaleForm = {
  projectId: "",
  propertyName: "",
  buyerName: "",
  buyerPhone: "",
  totalAmount: "",
  downPayment: "",
  installmentMonths: "12",
  planMode: "equal",
  planRows: [],
  note: "",
  ...emptyProof(),
};

export const saleToForm = (sale) => ({
  projectId: sale.project?.id || sale.project || "",
  propertyName: sale.propertyName || "",
  buyerName: sale.buyerName || "",
  buyerPhone: sale.buyerPhone || "",
  totalAmount: String(sale.totalAmount ?? ""),
  downPayment: String(sale.downPayment ?? ""),
  installmentMonths: String(sale.installmentMonths ?? "12"),
  planMode: "equal",
  planRows: [],
  note: sale.note || "",
  ...proofFromRecord({
    paymentType: sale.downPaymentType || sale.paymentType,
    referenceNumber: sale.downPaymentReference,
    image: sale.downPaymentImage,
    bank: sale.downPaymentBank || sale.bank,
  }),
});

export const saleMoneyChanged = (form, sale) =>
  Boolean(sale) &&
  (Number(form.totalAmount) !== Number(sale.totalAmount) ||
    Number(form.downPayment || 0) !== Number(sale.downPayment || 0));

export const salePayload = (
  form,
  { includeProject = true, includeMoney = true, includeDownPayment = true, includePlan = true } = {}
) => {
  const body = {
    propertyName: form.propertyName,
    buyerName: form.buyerName,
    buyerPhone: form.buyerPhone,
    note: form.note,
  };
  if (includeProject) body.projectId = form.projectId;
  if (includeMoney) {
    body.totalAmount = Number(form.totalAmount);
    if (includePlan) {
      if (form.planMode === "custom") {
        body.installments = customPlanPayload(form.planRows);
      } else {
        body.installmentMonths = Number(form.installmentMonths);
      }
    }
  }
  if (includeMoney && includeDownPayment) {
    body.downPayment = Number(form.downPayment || 0);
    if (body.downPayment > 0) withProof(body, form);
  }
  return body;
};

export function SaleForm({
  form,
  onChange,
  onSubmit,
  projects = [],
  showProject = true,
  moneyLocked = false,
  downPaymentLocked = false,
  showPlan = true,
}) {
  const set = (key) => (event) => onChange({ ...form, [key]: event.target.value });
  const preview = calcSalePreview(form);

  const setPlanMode = (planMode) => {
    if (planMode === "custom") {
      onChange({
        ...form,
        planMode,
        planRows:
          form.planRows?.length
            ? form.planRows
            : seedEqualRows({
                remaining: preview.remaining,
                months: form.installmentMonths,
                startDate: isoDate(),
              }),
      });
      return;
    }
    onChange({ ...form, planMode });
  };

  return (
    <>
      <form id="sale-form" className="form-grid" onSubmit={onSubmit}>
        {showProject ? (
          <Field label="Project" hint={projects.length === 0 ? "Make a project first, then add a sale." : undefined}>
            <Select required value={form.projectId} onChange={set("projectId")}>
              <option value="">Choose a project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} — {project.location}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Plot / unit">
          <Input required minLength={2} value={form.propertyName} onChange={set("propertyName")} placeholder="Unit A-12" />
        </Field>
        <Field label="Customer name">
          <Input required minLength={2} value={form.buyerName} onChange={set("buyerName")} placeholder="Ali Khan" />
        </Field>
        <Field label="Phone (optional)">
          <Input value={form.buyerPhone} onChange={set("buyerPhone")} placeholder="03xx" />
        </Field>
        <Field label="Total price">
          <Input
            type="number"
            min="0.01"
            step="0.01"
            required
            disabled={moneyLocked}
            value={form.totalAmount}
            onChange={set("totalAmount")}
          />
        </Field>
        <Field
          label="Down payment"
          hint={downPaymentLocked ? COPY.downPaymentLocked : "Goes into the daily book"}
        >
          <Input
            type="number"
            min="0"
            step="0.01"
            required
            disabled={moneyLocked || downPaymentLocked}
            value={form.downPayment}
            onChange={set("downPayment")}
          />
        </Field>
        {showPlan && preview.remaining > 0 ? (
          <Field label="Installment plan" wide>
            <Select
              disabled={moneyLocked}
              value={form.planMode || "equal"}
              onChange={(event) => setPlanMode(event.target.value)}
            >
              <option value="equal">Same amount every month</option>
              <option value="custom">Custom amounts and dates</option>
            </Select>
          </Field>
        ) : null}
        {showPlan && preview.remaining > 0 && form.planMode !== "custom" ? (
          <Field label="How many months?">
            <Input
              type="number"
              min="1"
              max="360"
              required
              disabled={moneyLocked}
              value={form.installmentMonths}
              onChange={set("installmentMonths")}
            />
          </Field>
        ) : null}
        {Number(form.downPayment) > 0 ? (
          <PaymentFields form={form} onChange={onChange} label="Down payment paid by" />
        ) : null}
        <Field label="Note" wide>
          <Textarea value={form.note} onChange={set("note")} />
        </Field>
        {showPlan && preview.remaining > 0 && form.planMode === "custom" ? (
          <div className="field-wide">
            <InstallmentPlanEditor
              rows={form.planRows || []}
              remaining={preview.remaining}
              disabled={moneyLocked}
              onChange={(planRows) => onChange({ ...form, planRows })}
            />
          </div>
        ) : null}
      </form>

      <div className={`hisaab ${preview.invalid ? "bad" : ""}`}>
        <h3>Customer's plan</h3>
        {preview.invalid && preview.down > preview.total ? (
          <p>Down payment cannot be more than the total price.</p>
        ) : null}
        <ul>
          <li>
            Total price <b>{formatMoney(preview.total)}</b>
          </li>
          <li>
            Down payment <b>{formatMoney(preview.down)}</b>
          </li>
          <li>
            Remaining <b>{formatMoney(preview.remaining)}</b>
          </li>
          {form.planMode === "custom" ? (
            <li>
              Custom plan total <b>{formatMoney(preview.customSum)}</b>
            </li>
          ) : (
            <li>
              {form.installmentMonths || 0} months · each month{" "}
              <b>{formatMoney(preview.monthly, { decimals: preview.monthly % 1 ? 2 : 0 })}</b>
            </li>
          )}
        </ul>
        <small>
          Remaining = {roundMoney(preview.total).toLocaleString("en-IN")} −{" "}
          {roundMoney(preview.down).toLocaleString("en-IN")} = {roundMoney(preview.remaining).toLocaleString("en-IN")}
        </small>
      </div>
    </>
  );
}

export const emptyExpenseForm = {
  category: "general",
  projectId: "",
  vendorId: "",
  title: "",
  amount: "",
  note: "",
  ...emptyProof(),
};

export const expenseToForm = (item) => ({
  category: item.category || "general",
  projectId: item.project?.id || "",
  vendorId: item.vendor?.id || item.vendor || "",
  title: item.title || "",
  amount: String(item.amount ?? ""),
  note: item.note || "",
  ...proofFromRecord(item),
});

export const expensePayload = (form, { editing = false } = {}) => {
  const body = withProof(
    {
      vendorId: form.vendorId,
      title: form.title,
      amount: Number(form.amount),
      note: form.note || "",
    },
    form
  );
  if (!editing) {
    body.category = form.category || "general";
    if (body.category === "project") body.projectId = form.projectId;
  }
  return body;
};

export function ExpenseForm({
  form,
  onChange,
  onSubmit,
  projects = [],
  vendors = [],
  editing = false,
}) {
  const set = (key) => (event) => onChange({ ...form, [key]: event.target.value });
  const selectedVendor = vendors.find((vendor) => vendor.id === form.vendorId);

  return (
    <form id="expense-form" className="form-grid" onSubmit={onSubmit}>
      {!editing ? (
        <Field label="Type">
          <Select
            value={form.category}
            onChange={(event) => onChange({ ...form, category: event.target.value, projectId: "" })}
          >
            <option value="general">Office / general</option>
            <option value="project">Project expense</option>
          </Select>
        </Field>
      ) : null}
      {!editing && form.category === "project" ? (
        <Field label="Project">
          <Select required value={form.projectId} onChange={set("projectId")}>
            <option value="">Choose a project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
      <Field label="Vendor" hint={vendors.length === 0 ? COPY.needVendor : undefined}>
        <Select required value={form.vendorId} onChange={set("vendorId")} disabled={vendors.length === 0}>
          <option value="">Choose a vendor</option>
          {vendors.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </Select>
      </Field>
      {selectedVendor ? (
        <div className="field-wide form-khata">
          <div className="card-head">
            <p className="muted">{selectedVendor.name} — khata so far</p>
            <Link to={`/vendors/${selectedVendor.id}`}>Open khata</Link>
          </div>
          <KhataSummary khata={selectedVendor.khata} variant="form" />
        </div>
      ) : null}
      <PaymentFields form={form} onChange={onChange} />
      <Field label="Title">
        <Input required minLength={2} value={form.title} onChange={set("title")} placeholder="Cement / salary" />
      </Field>
      <Field label="Amount">
        <Input type="number" min="0.01" step="0.01" required value={form.amount} onChange={set("amount")} />
      </Field>
      <Field label="Note" wide>
        <Textarea value={form.note} onChange={set("note")} />
      </Field>
    </form>
  );
}

export const emptyVendorForm = { name: "", phone: "", note: "" };
export const vendorToForm = (vendor) => ({
  name: vendor.name || "",
  phone: vendor.phone || "",
  note: vendor.note || "",
});
